import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermission } from "@/hooks/use-premission";
import {
  useAssignSenderPlan,
  useUnassignSenderPlan,
} from "@/hooks/use-b2c-sender";
import { useWarmupAccounts } from "@/hooks/use-mailbox";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import { cn, extractErrorMessage } from "@/lib/utils";
import { ISenderProfile, TargetAudience } from "@/types/mailbox.types";
import { ESPType, SubscriptionStatus } from "@/types/subscription.types";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

type Props = {
  senderId: string;
  senderEmail: string;
  subscriptionId: string | null;
  planId: string | null;
  addonPlanId: string | null;
  pageType: "list" | "details";
  className?: string;
  targetAudience?: TargetAudience | string;
  onSelect?: (
    type: "assign" | "unassign",
    data: { subscriptionId: string; planId: string }
  ) => void;
};

type SelectedPlanMetaData = {
  subscriptionId: string | null;
  planId: string | null;
};

const AssignSenderSubscriptionSelect = (props: Props) => {
  const { data: subscriptionPlans } = useGetActiveSubscriptionPlans();
  const [selectedPlanMetaData, setSelectedPlanMetaData] =
    useState<SelectedPlanMetaData | null>(null);
  const [selectedPlans, setSelectedPlans] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const { hasPermission } = usePermission();

  // Get all sender profiles to check which subscriptions are already assigned
  const { data: allSenderProfilesResponse } = useWarmupAccounts({ limit: 10000 });
  const allSenderProfiles = allSenderProfilesResponse?.data || [];

  const { mutateAsync: unassignPlan, isPending: isUnassigning } =
    useUnassignSenderPlan();
  const { mutateAsync: assignPlan, isPending: isAssigning } =
    useAssignSenderPlan();

  // Helper function to format ESP type name for display
  const formatEspTypeName = (espType: string): string => {
    const espTypeStr = String(espType).trim();
    const displayNames: Record<string, string> = {
      "Gmail": "Gmail",
      "gmail": "Gmail",
      "Outlook": "Outlook",
      "outlook": "Outlook",
      "Yahoo": "Yahoo",
      "yahoo": "Yahoo",
      "Google Workspace": "Google Workspace",
      "Google-Workspace": "Google Workspace",
      "google_workspace": "Google Workspace",
      "Microsoft 365": "Microsoft 365",
      "Microsoft365": "Microsoft 365",
      "microsoft_365": "Microsoft 365",
      "AOL": "AOL",
      "iCloud": "iCloud",
    };
    return displayNames[espTypeStr] || espTypeStr;
  };

  // Helper function to generate label for seed list plans
  const generateSeedListLabel = (espTypes: ESPType[], seedCountPerSender: number): string => {
    if (!espTypes || espTypes.length === 0) {
      return "Seed List";
    }
    
    // For single ESP type, show "Gmail Seeds (500)"
    if (espTypes.length === 1) {
      const espTypeName = formatEspTypeName(espTypes[0]);
      return `${espTypeName} Seeds (${seedCountPerSender.toLocaleString()})`;
    }
    
    // For multiple ESP types, show all with seed count
    const formattedEspTypes = espTypes.map(formatEspTypeName).join(", ");
    return `${formattedEspTypes} Seeds (${seedCountPerSender.toLocaleString()})`;
  };

  // Track which subscriptions are already assigned to other users
  const assignedSubscriptions = useMemo(() => {
    const assigned = new Set<string>();
    allSenderProfiles.forEach((profile: ISenderProfile) => {
      // Skip the current sender profile
      if (profile._id === props.senderId) return;
      
      // Track assigned subscription-plan combinations
      if (profile.subscriptionId && profile.planId) {
        assigned.add(`${profile.subscriptionId}-${profile.planId}`);
      }
      if (profile.subscriptionId && profile.addonPlan) {
        assigned.add(`${profile.subscriptionId}-${profile.addonPlan}`);
      }
    });
    return assigned;
  }, [allSenderProfiles, props.senderId]);

  const options = useMemo(() => {
    if (!subscriptionPlans) return [];

    // Flatten the nested structure and filter out null values
    const allOptions = subscriptionPlans
      .flatMap((subscription) =>
        subscription.plans.map((plan) => {
          // Handle custom plans
          if (plan?.isCustomPlan && plan?.addonPlanId?._id) {
            return {
              key: `${subscription._id}-${plan.addonPlanId._id}`,
              label: `Custom Plan (${plan.addonPlanId.maxEmailsPerDay} emails/day)`,
              value: plan.addonPlanId._id,
              type: "custom" as const,
              emailsPerDay: plan.addonPlanId.maxEmailsPerDay,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used: plan.assignedMailboxes?.length || 0,
              total: plan.quantity,
              category: "B2C", // Default to B2C for custom plans
              isSeedListPlan: false,
              espTypes: [] as ESPType[],
              seedCountPerSender: 0,
            };
          }

          // Handle regular plans
          if (plan?.planId?._id) {
            const isSeedListPlan = plan.planId.isOnDemandSenderProfile || false;
            const espTypes = (plan.planId.espTypes || plan.planId.senderProfileMetadata?.espTypes || []) as ESPType[];
            const seedCountPerSender = plan.planId.seedCountPerSender || plan.planId.senderProfileMetadata?.baseSeedCount || 0;
            
            // Generate label based on plan type
            let label: string;
            if (isSeedListPlan && espTypes.length > 0) {
              label = generateSeedListLabel(espTypes, seedCountPerSender);
            } else {
              label = `${plan.planId.category} - ${plan.planId.planType}`;
            }
            
            // For seed list plans, use usedSenderProfilesMonthly and maxSenderProfilesMonthly
            const used = isSeedListPlan
              ? (plan.usedSenderProfilesMonthly || 0)
              : (plan.assignedMailboxes?.length || 0);
            const total = isSeedListPlan
              ? (plan.maxSenderProfilesMonthly || plan.quantity || 0)
              : plan.quantity;
            
            return {
              key: `${subscription._id}-${plan.planId._id}`,
              label,
              value: plan.planId._id,
              type: "regular" as const,
              category: plan.planId.category,
              planType: plan.planId.planType,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used,
              total,
              isSeedListPlan,
              espTypes,
              seedCountPerSender,
            };
          }

          return null;
        })
      )
      .filter(
        (option): option is NonNullable<typeof option> => option !== null
      );

    // Filter to only show seed list plans for sender profiles
    const seedListOptions = allOptions.filter((option) => option.isSeedListPlan === true);

    // Filter by target audience if provided
    if (props.targetAudience) {
      const audience = props.targetAudience.toLowerCase();
      if (audience === "b2b" || audience === "b2c") {
        return seedListOptions.filter((option) => {
          const optionCategory = option.category?.toLowerCase() || "b2c";
          return optionCategory === audience;
        });
      }
      // If targetAudience is "both", show all seed list options
      return seedListOptions;
    }

    return seedListOptions;
  }, [subscriptionPlans, props.targetAudience]);

  const selectedOption = options?.find(
    (option) => option.value === selectedPlanMetaData?.planId
  );

  const handleUnassign = async () => {
    if (!props.subscriptionId) {
      toast.error(
        "No subscription to unassign, please assign a subscription first"
      );
      return;
    }

    try {
      await unassignPlan(props.senderId);

      setSelectedPlanMetaData(null);
      setSelectedPlans(new Set()); // Clear selected plans
      optimisticUpdate(queryClient, "unassign", props.pageType, props.senderId);

      toast.success("Subscription unassigned successfully");
      window.cioanalytics?.track("sender_profile_subscription_unassigned", {
        subscriptionId: props.subscriptionId,
        planId: props.planId || props.addonPlanId,
        email: props.senderEmail,
      });

      if (props.onSelect) {
        props.onSelect("unassign", {
          subscriptionId: props.subscriptionId,
          planId: props.planId || props.addonPlanId || "",
        });
      }
      
      setIsOpen(false);
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to unassign plan.");
      toast.error(message);
    }
  };

  const handleTogglePlan = (optionKey: string, subscriptionId: string, planId: string) => {
    const newSelectedPlans = new Set(selectedPlans);
    if (newSelectedPlans.has(optionKey)) {
      newSelectedPlans.delete(optionKey);
    } else {
      newSelectedPlans.add(optionKey);
    }
    setSelectedPlans(newSelectedPlans);
  };

  const handleApplyAssignments = async () => {
    try {
      const assignments = Array.from(selectedPlans).map((key) => {
        const [subscriptionId, planId] = key.split('-');
        const option = options.find((opt) => opt.key === key);
        return { subscriptionId, planId, option };
      }).filter((assignment): assignment is { subscriptionId: string; planId: string; option: NonNullable<typeof options[0]> } => {
        return assignment.option !== undefined;
      });

      if (assignments.length === 0) {
        // If no plans selected, unassign current
        if (props.subscriptionId) {
          await handleUnassign();
        }
        setIsOpen(false);
        return;
      }

      // Validate that all selected plans have available slots
      const invalidAssignments = assignments.filter((assignment) => {
        const remaining = assignment.option.total - assignment.option.used;
        return remaining <= 0;
      });

      if (invalidAssignments.length > 0) {
        const planNames = invalidAssignments.map((a) => a.option.label).join(", ");
        toast.error(
          `Cannot assign: ${planNames}. No remaining slots available.`
        );
        return;
      }

      // Unassign current plan if there's one assigned and it's not in the new selection
      if (props.subscriptionId && (props.planId || props.addonPlanId)) {
        const currentPlanId = props.planId || props.addonPlanId;
        const currentKey = `${props.subscriptionId}-${currentPlanId}`;
        if (!selectedPlans.has(currentKey)) {
          // Current plan is not in new selection, unassign it first
          try {
            await unassignPlan(props.senderId);
          } catch (error) {
            console.error("Failed to unassign current plan:", error);
            // Continue with assignments even if unassign fails
          }
        }
      }

      // Assign all selected plans (one API call per plan)
      // Use Promise.allSettled to handle partial failures
      const assignmentPromises = assignments.map((assignment) =>
        assignPlan({
          id: props.senderId,
          subscriptionId: assignment.subscriptionId,
          planId: assignment.planId,
        })
      );

      const assignmentResults = await Promise.allSettled(assignmentPromises);

      // Map results back to assignments with their indices
      const resultsWithAssignments = assignmentResults.map((result, index) => ({
        result,
        assignment: assignments[index],
      }));

      // Check for failures
      const failures = resultsWithAssignments.filter(
        (item) => item.result.status === "rejected"
      );
      const successes = resultsWithAssignments.filter(
        (item) => item.result.status === "fulfilled"
      );

      if (failures.length > 0) {
        // Extract error messages from failures
        const errorMessages = failures.map(({ result, assignment }) => {
          if (result.status === "rejected") {
            const error = result.reason;
            const errorMsg = extractErrorMessage(error, "Failed to assign plan");
            // Try to get more detailed error from response
            let detailedError = errorMsg;
            if (error && typeof error === 'object' && 'response' in error) {
              const axiosError = error as any;
              if (axiosError.response?.data?.message) {
                detailedError = axiosError.response.data.message;
              } else if (axiosError.response?.data?.error) {
                detailedError = axiosError.response.data.error;
              }
            }
            // Include which plan failed
            return `${assignment.option.label}: ${detailedError}`;
          }
          return "Unknown error";
        });

        // Show specific error messages
        if (failures.length === assignments.length) {
          // All failed - show all error messages
          const errorText = errorMessages.length === 1 
            ? errorMessages[0] 
            : errorMessages.join("; ");
          toast.error(errorText || "Failed to assign plan(s)");
        } else {
          // Partial failure
          const errorText = errorMessages.join("; ");
          toast.error(
            `${successes.length} assigned, ${failures.length} failed. ${errorText}`
          );
        }

        // If some succeeded, still update the UI
        if (successes.length > 0) {
          optimisticUpdate(queryClient, "assign", props.pageType, props.senderId);
        }
        return;
      }

      // Set the first assignment as the primary one for display
      const firstAssignment = assignments[0];
      setSelectedPlanMetaData({
        subscriptionId: firstAssignment.subscriptionId,
        planId: firstAssignment.planId,
      });

      optimisticUpdate(queryClient, "assign", props.pageType, props.senderId);

      toast.success(
        assignments.length === 1
          ? "Subscription assigned successfully"
          : `${assignments.length} subscriptions assigned successfully`
      );

      // Track analytics for all assignments
      assignments.forEach((assignment) => {
        window.cioanalytics?.track("sender_profile_subscription_assigned", {
          subscriptionId: assignment.subscriptionId,
          planId: assignment.planId,
          email: props.senderEmail,
        });
      });

      if (props.onSelect && assignments.length > 0) {
        props.onSelect("assign", {
          subscriptionId: firstAssignment.subscriptionId,
          planId: firstAssignment.planId,
        });
      }

      setIsOpen(false);
    } catch (error) {
      // This catch block handles any unexpected errors
      const message = extractErrorMessage(error, "Failed to assign plan(s).");
      toast.error(message);
      console.error("Assignment error:", error);
    }
  };

  const handleAssign = async ({
    subscriptionId,
    planId,
  }: {
    subscriptionId: string;
    planId: string;
  }) => {
    try {
      if (!subscriptionId || !planId) {
        toast.error("Provide a valid subscription and plan");
        return;
      }

      await assignPlan({
        id: props.senderId,
        subscriptionId,
        planId,
      });

      setSelectedPlanMetaData({
        subscriptionId,
        planId,
      });

      optimisticUpdate(queryClient, "assign", props.pageType, props.senderId);

      toast.success("Subscription assigned successfully");
      window.cioanalytics?.track("sender_profile_subscription_assigned", {
        subscriptionId,
        planId,
        email: props.senderEmail,
      });

      if (props.onSelect) {
        props.onSelect("assign", { subscriptionId, planId });
      }
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to assign plan.");
      toast.error(message);
    }
  };

  useEffect(() => {
    setSelectedPlanMetaData({
      subscriptionId: props.subscriptionId,
      planId: props.planId || props.addonPlanId,
    });
    
    // Initialize selected plans with current assignment
    if (props.subscriptionId && (props.planId || props.addonPlanId)) {
      const planId = props.planId || props.addonPlanId;
      setSelectedPlans(new Set([`${props.subscriptionId}-${planId}`]));
    } else {
      setSelectedPlans(new Set());
    }
  }, [props.subscriptionId, props.planId, props.addonPlanId]);

  const isLoading = isUnassigning || isAssigning;

  if (!hasPermission("mailboxes:edit")) {
    return (
      <div className={"text-sm text-muted-foreground"}>
        {selectedOption
          ? `${selectedOption?.label} (${selectedOption?.interval})`
          : "No subscription assigned"}
      </div>
    );
  }
  // Check if option is assigned to another user
  const isAssignedToOther = (optionKey: string) => {
    return assignedSubscriptions.has(optionKey);
  };

  // Check if option is selected
  const isSelected = (optionKey: string) => {
    return selectedPlans.has(optionKey);
  };

  // Get display text for button
  const getButtonText = () => {
    if (selectedPlans.size === 0) {
      return "Select Subscription";
    }
    if (selectedPlans.size === 1) {
      const selectedKey = Array.from(selectedPlans)[0];
      const option = options.find((opt) => opt.key === selectedKey);
      if (option) {
        return `${option.label} (${option.interval})`;
      }
    }
    return `${selectedPlans.size} subscription${selectedPlans.size > 1 ? 's' : ''} selected`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-between", props.className)}
          disabled={isLoading}
        >
          <span className="text-left truncate">
            {getButtonText()}
          </span>
          {isLoading ? (
            <Loader2 className="w-4 h-4 ml-2 animate-spin shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[400px] overflow-y-auto">
        {options?.map((option) => {
          const isAssigned = isAssignedToOther(option.key);
          const isOptionSelected = isSelected(option.key);
          const isDisabled = 
            option.status === SubscriptionStatus.PENDING ||
            isAssigned ||
            (option.total - option.used <= 0 && !isOptionSelected);

          return (
            <DropdownMenuItem
              key={option.key}
              onSelect={(e) => {
                e.preventDefault();
                if (!isDisabled) {
                  handleTogglePlan(option.key, option.subscriptionId, option.value);
                }
              }}
              className="flex flex-col gap-0 items-start hover:bg-accent cursor-pointer"
              disabled={isDisabled}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={isOptionSelected}
                    onCheckedChange={(checked) => {
                      if (!isDisabled) {
                        handleTogglePlan(option.key, option.subscriptionId, option.value);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isDisabled}
                    className="shrink-0"
                  />
                  <span className="font-medium truncate">{option.label}</span>
                </div>
                {isOptionSelected && !isAssigned && (
                  <Check className="w-4 h-4 shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground pl-6">
                <span>{option.interval}</span>
                <span>•</span>
                <span>{option.total - option.used} remaining</span>
                {option.status === SubscriptionStatus.PENDING && (
                  <Badge
                    variant="default"
                    className="text-xs ml-2 bg-red-100 text-red-500 border-none shadow-none"
                  >
                    Unpaid
                  </Badge>
                )}
                {isAssigned && (
                  <Badge
                    variant="default"
                    className="text-xs ml-2 bg-yellow-100 text-yellow-600 border-none shadow-none"
                  >
                    Assigned
                  </Badge>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
        {options?.length === 0 && (
          <DropdownMenuItem disabled>
            <span className="text-gray-500">No options available</span>
          </DropdownMenuItem>
        )}

        {/* Apply button for multi-select */}
        {selectedPlans.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button
                onClick={handleApplyAssignments}
                className="w-full"
                disabled={isLoading}
                size="sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  `Apply ${selectedPlans.size} selection${selectedPlans.size > 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </>
        )}

        {/* Unassign Subscription */}
        {selectedOption && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => handleUnassign()}
              className="justify-between text-destructive hover:!text-destructive hover:!bg-destructive/10 cursor-pointer"
            >
              <span>Unassign Subscription</span>
              <X className="w-4 h-4 ml-2" />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const optimisticUpdate = (
  queryClient: QueryClient,
  type: "unassign" | "assign",
  pageType: "list" | "details",
  senderId: string
) => {
  if (pageType === "list") {
    try {
      // Invalidate warmupAccounts queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
    } catch (error) {
      console.error("failed to optimistic update, invalidating queries");
      queryClient.invalidateQueries({ queryKey: ["warmupAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["b2cSenders"] });
    }
  } else {
    queryClient.invalidateQueries({
      queryKey: ["b2cSender", senderId],
    });
    queryClient.invalidateQueries({
      queryKey: ["warmupAccounts"],
    });
  }
};

export default AssignSenderSubscriptionSelect;

